import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { apiErrorCode, crmErrorMessage } from "../api/crmErrors";
import { CRM_ERRORS, SEARCH_MIN_CHARS, SOURCE_KEYS } from "../contract";
import CrmAlertDialog from "../ui/CrmAlertDialog";
import CrmDialog from "../ui/CrmDialog";
import CrmInput from "../ui/CrmInput";
import CrmButton from "../ui/CrmButton";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

/**
 * "Erase client data" (spec Privacy): what will be deleted, what remains, the
 * suppression list; confirmed by typing the client's FIRST NAME exactly (trimmed). The
 * erasure is a hard delete and is logged server-side.
 */
export function EraseClientDialog({ client, onClose, onErased }) {
  const t = useT("crm");
  const tc = useT("common");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const name = client.first_name || client.display_name || "";
  const matches = typed.trim() === name && name !== "";

  const erase = async () => {
    if (!matches || busy) return;
    setBusy(true);
    try {
      await crmApi.eraseClient(client.id, typed.trim());
      toast.success(t("privacy.erased"));
      onErased?.();
      onClose();
    } catch (e) {
      if (apiErrorCode(e) === CRM_ERRORS.confirmNameMismatch) setError(t("errors.confirmNameMismatch"));
      else toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmAlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={t("privacy.eraseTitle", { name })}
      testId={CRM.clientErase}
      confirmLabel={t("privacy.erase")}
      busy={busy}
      confirmDisabled={!matches}
      onConfirm={erase}
      confirmTestId={CRM.clientEraseConfirm}
    >
      <p><strong>{t("privacy.willDeleteLabel")}</strong> {t("privacy.willDelete")}</p>
      <p className="mt-2"><strong>{t("privacy.willRemainLabel")}</strong> {t("privacy.willRemain")}</p>
      <p className="mt-2"><strong>{t("privacy.alsoLabel")}</strong> {t("privacy.also")}</p>
      <label htmlFor="crm-erase-name" className="mt-4 block text-xs text-eh-muted">{t("privacy.typeToConfirm", { name })}</label>
      <CrmInput id="crm-erase-name" className="mt-1 w-auto" value={typed} autoComplete="off" invalid={!!error}
        onChange={(e) => { setTyped(e.target.value); setError(""); }} data-testid={CRM.clientEraseInput} />
      {error && <p role="alert" className="mt-1 text-xs text-red-700">{error}</p>}
    </CrmAlertDialog>
  );
}

/** "Delete client (Owner only, confirmation)": removes the CRM record; no suppression. */
export function DeleteClientDialog({ client, onClose, onDeleted }) {
  const t = useT("crm");
  const tc = useT("common");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await crmApi.deleteClient(client.id);
      toast.success(t("privacy.deleted"));
      onDeleted?.();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
      setBusy(false);
    }
  };
  return (
    <CrmAlertDialog open onOpenChange={(o) => { if (!o) onClose(); }} testId={CRM.clientDelete}
      title={t("privacy.deleteTitle", { name: client.display_name || client.primary_email })}
      description={t("privacy.deleteBody")} confirmLabel={t("privacy.delete")} tone="danger" busy={busy}
      onConfirm={run} confirmTestId={CRM.clientDeleteConfirm} />
  );
}

/** "Unsubscribe client": consent flips to Unsubscribed and marketing sequences halt. */
export function UnsubscribeDialog({ client, onClose, onDone }) {
  const t = useT("crm");
  const tc = useT("common");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const saved = await crmApi.unsubscribeClient(client.id);
      toast.success(t("privacy.unsubscribed"));
      onDone?.(saved);
      onClose();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };
  return (
    <CrmAlertDialog open onOpenChange={(o) => { if (!o) onClose(); }} testId={CRM.clientUnsubscribe}
      title={t("privacy.unsubscribeTitle", { name: client.first_name || client.display_name })}
      description={t("privacy.unsubscribeBody")} confirmLabel={t("privacy.unsubscribe")} busy={busy}
      onConfirm={run} confirmTestId={CRM.clientUnsubscribeConfirm} />
  );
}

/** One side of the merge preview (wireframe: name, email, source). */
function MergeSide({ label, record }) {
  const t = useT("crm");
  if (!record) return null;
  return (
    <div className="rounded-2xl bg-eh-cream p-3 text-sm">
      <p className="mb-1 text-xs uppercase tracking-wider text-eh-muted">{label}</p>
      <p className="font-medium">{record.display_name}</p>
      {(record.emails || [record.primary_email]).filter(Boolean).map((e) => <p key={e} className="truncate">{e}</p>)}
      {record.phone && <p>{record.phone}</p>}
      {SOURCE_KEYS[record.source] && <p>{t(SOURCE_KEYS[record.source])}</p>}
      {record.tags?.length > 0 && <p className="text-eh-muted">{record.tags.join(", ")}</p>}
    </div>
  );
}

/**
 * "Merge duplicate" (spec): search the other record, side-by-side preview (primary wins,
 * the other fills blanks; emails and tags unioned), "Confirm merge".
 */
export function MergeDuplicateDialog({ client, onClose, onMerged }) {
  const t = useT("crm");
  const tc = useT("common");
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [other, setOther] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < SEARCH_MIN_CHARS) { setCandidates([]); return undefined; }
    let alive = true;
    const id = setTimeout(() => {
      crmApi.listMergeCandidates(client.id, q.trim())
        .then((page) => { if (alive) setCandidates((page?.rows || []).filter((r) => r.id !== client.id)); })
        .catch((e) => { if (alive) toast.error(crmErrorMessage(e, t, tc, "errors.loadFailed")); });
    }, 300);
    return () => { alive = false; clearTimeout(id); };
  }, [q, client.id, t, tc]);

  useEffect(() => {
    if (!other) { setPreview(null); return undefined; }
    let alive = true;
    crmApi.getMergePreview(client.id, other.id)
      .then((p) => { if (alive) setPreview(p); })
      .catch((e) => { if (alive) toast.error(crmErrorMessage(e, t, tc, "errors.loadFailed")); });
    return () => { alive = false; };
  }, [other, client.id, t, tc]);

  const merge = async () => {
    setBusy(true);
    try {
      const saved = await crmApi.mergeClients(client.id, other.id);
      toast.success(t("merge.done"));
      onMerged?.(saved);
      onClose();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  return (
    <CrmDialog open onOpenChange={(o) => { if (!o) onClose(); }} size="lg" title={t("merge.title")} testId={CRM.clientMerge}
      footer={(
        <>
          <CrmButton variant="link" onClick={onClose}>{t("common.cancel")}</CrmButton>
          <CrmButton onClick={merge} disabled={!preview || busy} data-testid={CRM.clientMergeConfirm}>{t("merge.confirm")}</CrmButton>
        </>
      )}>
      <label htmlFor="crm-merge-q" className={LABEL}>{t("merge.search")}</label>
      <CrmInput id="crm-merge-q" type="search" value={q} onChange={(e) => { setQ(e.target.value); setOther(null); }} data-testid={CRM.clientMergeSearch} />
      {!other && candidates.length > 0 && (
        <ul className="mt-2 space-y-1">
          {candidates.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => setOther(c)} data-testid={CRM.clientMergeCandidate(c.id)}
                className="w-full rounded-2xl px-3 py-2 text-left text-sm hover:bg-eh-cream">
                <span className="font-medium">{c.display_name}</span> · {c.primary_email}
              </button>
            </li>
          ))}
        </ul>
      )}
      {preview && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <MergeSide label={t("merge.keep")} record={preview.primary} />
          <MergeSide label={t("merge.absorb")} record={preview.other} />
          <div className="sm:col-span-2"><MergeSide label={t("merge.result")} record={preview.result} /></div>
        </div>
      )}
    </CrmDialog>
  );
}
